import { createFileRoute, Link } from "@tanstack/react-router";
import { useUser, useClerk } from "@clerk/clerk-react";
import { getUserFromClerkId, type User, deleteUser } from "@/hooks/useUser";
import { BrandedSpinner } from "@/components/BrandedSpinner";
import { useEffect, useMemo, useRef, useState } from "react";
import FamilySection from "@/components/family/FamilySection";
import FamilyMemberExpanded from "@/components/family/FamilyMemberExpanded";
import { useSponsorProgress } from "@/hooks/useSponsorProgress";
import ProfileTab from "@/components/account/ProfileTab";
import PreferencesTab from "@/components/account/PreferencesTab";
import SubscriptionTab from "@/components/account/SubscriptionTab";
import WelcomeKitSection from "@/components/account/WelcomeKitSection";
import SavedContentSection from "@/components/account/SavedContentSection";
import DeleteUserModal from "@/components/DeleteUserModal";
import Button from "@/components/Button";
import { ArrowRight, Bell, Bookmark, CreditCard, Gift, LayoutDashboard, LogOut, UserCircle2, UsersRound, type LucideIcon } from "lucide-react";
import { calculateUsedLicenses, getUserLicenseQuantity } from "@/lib/userSubscriptionHelpers";

const SPONSOR_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    description: "Plan, role, and account snapshot",
  },
  {
    id: "profile",
    label: "Profile",
    description: "Personal information and avatar",
  },
  {
    id: "family",
    label: "Plan",
    description: "Household members and invitations",
  },
  {
    id: "subscription",
    label: "Subscription",
    description: "Billing and account controls",
  },
  {
    id: "welcomeKit",
    label: "Welcome Kit",
    description: "Starter materials and delivery",
  },
  {
    id: "saved",
    label: "Saved",
    description: "Saved guides and articles",
  },
  {
    id: "preferences",
    label: "Preferences",
    description: "Communication and privacy settings",
  },
] as const;

const CHILD_SECTIONS = [
  {
    id: "overview",
    label: "Overview",
    description: "Account snapshot and access level",
  },
  {
    id: "profile",
    label: "Profile",
    description: "Personal information and avatar",
  },
  {
    id: "family",
    label: "Family",
    description: "Connected family members and account controls",
  },
  {
    id: "preferences",
    label: "Preferences",
    description: "Communication and privacy settings",
  },
] as const;

type AccountSectionId = (typeof SPONSOR_SECTIONS)[number]["id"] | (typeof CHILD_SECTIONS)[number]["id"];
type AccountSection = (typeof SPONSOR_SECTIONS)[number] | (typeof CHILD_SECTIONS)[number];
type SectionMeta = { label: string; icon: LucideIcon };

const TAB_TO_SECTION: Record<string, AccountSectionId> = {
  profile: "profile",
  family: "family",
  preferences: "preferences",
  subscription: "subscription",
};

const SECTION_TO_TAB: Partial<Record<AccountSectionId, string>> = {
  profile: "profile",
  family: "family",
  preferences: "preferences",
  subscription: "subscription",
};

function getSectionMeta(isFamilyPlan: boolean): Record<AccountSectionId, SectionMeta> {
  return {
    overview: { label: "Overview", icon: LayoutDashboard },
    profile: { label: "Profile", icon: UserCircle2 },
    family: { label: isFamilyPlan ? "Family Plan" : "Plan", icon: UsersRound },
    preferences: { label: "Preferences", icon: Bell },
    welcomeKit: { label: "Welcome Kit", icon: Gift },
    saved: { label: "Saved", icon: Bookmark },
    subscription: { label: "Billing", icon: CreditCard },
  };
}

export const Route = createFileRoute("/_auth/account")({
  validateSearch: (search: Record<string, unknown>) => {
    const result: {
      tab?: string;
      message?: string;
      error?: string;
      invitationcode?: string;
      __clerk_ticket?: string;
    } = {};
    if (typeof search.tab === "string") result.tab = search.tab;
    if (typeof search.message === "string") result.message = search.message;
    if (typeof search.error === "string") result.error = search.error;
    if (typeof search.invitationcode === "string") result.invitationcode = search.invitationcode;
    if (typeof search.__clerk_ticket === "string") result.__clerk_ticket = search.__clerk_ticket;
    return result;
  },
  component: Account,
});

export interface UserState extends User {
  email: string;
  isCurrentUser: boolean;
}

function Account() {
  const { user: clerkUser } = useUser();
  const { signOut } = useClerk();
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: userData, isLoading } = getUserFromClerkId(clerkUser?.id ?? '', { enabled: !!clerkUser });
  const userEmail = clerkUser?.emailAddresses?.[0]?.emailAddress || "";

  const isChildUser = !!userData?.parentUserId;
  const sections = useMemo<AccountSection[]>(
    () => (isChildUser ? [...CHILD_SECTIONS] : [...SPONSOR_SECTIONS]),
    [isChildUser]
  );
  const [activeSection, setActiveSection] = useState<AccountSectionId>(sections[0].id);
  const sectionRefs = useRef<Partial<Record<AccountSectionId, HTMLElement | null>>>({});

  const registerSectionRef = (sectionId: AccountSectionId) => (node: HTMLElement | null) => {
    sectionRefs.current[sectionId] = node;
  };

  const userInitials =
    userData?.name
      ?.split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "G";

  const scrollToSection = (sectionId: AccountSectionId) => {
    const target = sectionRefs.current[sectionId];
    if (!target) return;
    target.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(sectionId);
    navigate({
      search: (prev) => ({ ...prev, tab: SECTION_TO_TAB[sectionId] }),
      replace: false,
    });
  };

  useEffect(() => {
    const fromSearch = searchParams.tab ? TAB_TO_SECTION[searchParams.tab] : undefined;
    const resolvedSection = sections.find((section) => section.id === fromSearch)?.id ?? sections[0].id;
    setActiveSection(resolvedSection);
    const target = sectionRefs.current[resolvedSection];
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [searchParams.tab, sections]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visibleEntries.length === 0) return;
        const topSectionId = visibleEntries[0].target.getAttribute("data-account-section-id") as AccountSectionId | null;
        if (topSectionId) {
          setActiveSection(topSectionId);
        }
      },
      {
        rootMargin: "-20% 0px -55% 0px",
        threshold: [0.2, 0.35, 0.6],
      }
    );

    sections.forEach((section) => {
      const node = sectionRefs.current[section.id];
      if (node) observer.observe(node);
    });

    return () => {
      observer.disconnect();
    };
  }, [sections]);

  // Child user: family progress + deactivate state
  const { data: childFamilyData, isLoading: childFamilyLoading } = useSponsorProgress({ enabled: !!isChildUser });
  const deleteUserMutation = deleteUser();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  interface UserToDelete { id: string; email: string; isSelf: boolean; isChild?: boolean; }
  const [userToDelete, setUserToDelete] = useState<UserToDelete | null>(null);

  const confirmDeleteUser = async (_options?: { alsoDowngrade?: boolean }) => {
    if (!userToDelete) return;
    await deleteUserMutation.mutateAsync();
    await signOut();
    setShowDeleteConfirmModal(false);
    setUserToDelete(null);
  };

  // Compute plan state before early returns so hook count is stable
  const totalLicenses = getUserLicenseQuantity(userData);
  const usedLicenses = calculateUsedLicenses(userData);
  const hasInvitations = (userData?.invitations ?? []).length > 0;
  const hasChildUsers = (userData?.childUsers ?? []).length > 0;
  const isFamilyPlan = totalLicenses > 1 || isChildUser || hasInvitations || hasChildUsers;
  const sectionMeta = useMemo(() => getSectionMeta(isFamilyPlan), [isFamilyPlan]);

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100dvh-56px)] md:min-h-[calc(100dvh-64px)] items-center justify-center">
        <BrandedSpinner size="xl" />
      </div>
    );
  }

  if (!clerkUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f5f5f7]">
        <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
          <p className="text-slate-600">Unable to load user information. Please log in again.</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f5f5f7]">
        <div className="mx-4 w-full max-w-md rounded-2xl bg-white p-6 text-center shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]">
          <p className="text-slate-600">
            Issue retrieving your account information, please try refreshing the page or contacting support.
          </p>
        </div>
      </div>
    );
  }

  const memberSince = userData?.createdAt
    ? new Date(userData.createdAt).toLocaleDateString("en-GB", {
        month: "long",
        year: "numeric",
      })
    : "Unknown";
  const pendingInvites = userData.invitations?.filter((inv) => inv.status === "PENDING").length ?? 0;
  const exp = Math.max(0, userData.exp ?? 0);
  const currentLevel = Math.floor(exp / 100) + 1;
  const nextLevelExp = currentLevel * 100;
  const pointsToNextLevel = nextLevelExp - exp;
  const progress = (exp % 100) / 100;
  const activeSubscription = (userData.subscriptions ?? []).find((sub: any) => {
    const status = String(sub?.status ?? "").toUpperCase();
    return status === "ACTIVE" || status === "TRIALING";
  });
  const billingInterval = String(activeSubscription?.billingInterval ?? "").toUpperCase();
  const billingCadenceLabel = isChildUser
    ? "Managed by family plan"
    : billingInterval === "YEARLY"
      ? "Annual"
      : billingInterval === "MONTHLY"
        ? "Monthly"
        : "Not set";
  const planLabel = isChildUser
    ? "Family member"
    : isFamilyPlan
      ? "Family plan"
      : userData.license === "PAID"
        ? "Individual plan"
        : "Free plan";
  const planMembersLabel =
    totalLicenses > 0 ? `${usedLicenses} of ${totalLicenses} seats used` : "No paid seats active";

  return (
    <div className="min-h-screen bg-white">
      <div className="mx-auto max-w-6xl px-6 pb-16 pt-10 sm:px-8 sm:pt-12 lg:px-10">
        {(searchParams.message || searchParams.error) && (
          <div className="mb-6 space-y-2">
            {searchParams.message && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                {searchParams.message}
              </div>
            )}
            {searchParams.error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                {searchParams.error}
              </div>
            )}
          </div>
        )}

        <div className="grid items-start gap-6 md:grid-cols-[260px_minmax(0,1fr)]">
          <aside className="hidden self-start md:sticky md:top-24 md:block md:border-r md:border-slate-200/60 md:pr-6">
            <div className="max-h-[calc(100vh-7rem)] overflow-x-hidden overflow-y-auto">
              <div className="mb-3 rounded-2xl bg-slate-50 p-3.5">
                <div className="flex items-center gap-3">
                  {clerkUser.imageUrl ? (
                    <img
                      src={clerkUser.imageUrl}
                      alt=""
                      className="h-11 w-11 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-700">
                      {userInitials}
                    </div>
                  )}
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-slate-800">
                      {userData.name || "GranGuard User"}
                    </p>
                    <p className="truncate text-sm text-slate-400">{userEmail}</p>
                  </div>
                </div>
              </div>
              <nav className="space-y-0.5" aria-label="Account sections">
                {sections.map((section) => (
                  (() => {
                    const meta = sectionMeta[section.id];
                    const Icon = meta.icon;
                    const isActive = activeSection === section.id;
                    return (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => scrollToSection(section.id)}
                        className={`group flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                          isActive
                            ? "bg-slate-50 text-slate-900"
                            : "text-slate-600 hover:bg-slate-50/60 hover:text-slate-900"
                        }`}
                      >
                        <Icon
                          className={`h-[18px] w-[18px] ${
                            isActive ? "text-violet-600" : "text-slate-400 group-hover:text-slate-600"
                          }`}
                        />
                        <span className="text-sm font-medium leading-none">
                          {meta.label}
                        </span>
                      </button>
                    );
                  })()
                ))}
              </nav>
              <div className="mt-2 border-t border-slate-200/60 pt-2">
                <button
                  type="button"
                  onClick={() => signOut()}
                  className="group flex w-full cursor-pointer items-center gap-3 rounded-xl px-3 py-2.5 text-left text-slate-500 transition-colors hover:bg-slate-50/60 hover:text-slate-900"
                >
                  <LogOut className="h-[18px] w-[18px] text-slate-400 group-hover:text-slate-600" />
                  <span className="text-sm font-medium leading-none">Sign out</span>
                </button>
              </div>
            </div>
          </aside>

          <main className="divide-y divide-slate-200/60">
            {/* Section tabs hidden on mobile — sections have clear headers, just scroll */}

            <section
              id="account-overview"
              data-account-section-id="overview"
              ref={registerSectionRef("overview")}
              className="scroll-mt-28 py-8 first:pt-0 sm:py-10"
            >
              <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Overview</h2>
              <p className="mt-1.5 text-base text-slate-500">
                A quick snapshot of your account setup, subscription, and learning progress.
              </p>
              <div className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2 xl:grid-cols-5">
                <div className="border-b border-slate-200/80 pb-3 xl:border-b-0 xl:border-r xl:border-slate-100 xl:pb-0 xl:pr-6">
                  <p className="text-sm font-medium text-slate-500">Active since</p>
                  <p className="mt-1 text-[17px] font-medium tracking-tight text-slate-900">{memberSince}</p>
                </div>
                <div className="border-b border-slate-200/80 pb-3 xl:border-b-0 xl:border-r xl:border-slate-100 xl:pb-0 xl:pr-6">
                  <p className="text-sm font-medium text-slate-500">Plan</p>
                  <p className="mt-1 text-[17px] font-medium tracking-tight text-slate-900">{planLabel}</p>
                </div>
                <div className="border-b border-slate-200/80 pb-3 xl:border-b-0 xl:border-r xl:border-slate-100 xl:pb-0 xl:pr-6">
                  <p className="text-sm font-medium text-slate-500">Billing cadence</p>
                  <p className="mt-1 text-[17px] font-medium tracking-tight text-slate-900">{billingCadenceLabel}</p>
                </div>
                <div className="border-b border-slate-200/80 pb-3 xl:border-b-0 xl:border-r xl:border-slate-100 xl:pb-0 xl:pr-6">
                  <p className="text-sm font-medium text-slate-500">Plan members</p>
                  <p className="mt-1 text-[17px] font-medium tracking-tight text-slate-900">{planMembersLabel}</p>
                </div>
                <div className="pb-1">
                  <p className="text-sm font-medium text-slate-500">Pending invites</p>
                  <p className="mt-1 text-[17px] font-medium tracking-tight text-slate-900">{pendingInvites}</p>
                </div>
              </div>

              <div className="mt-6 border-t border-slate-100 py-4">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Progress</p>
                    <p className="mt-1 text-[17px] font-medium tracking-tight text-slate-900">
                      Level {currentLevel} • {exp.toLocaleString()} points
                    </p>
                  </div>
                  <p className="text-sm text-slate-500">
                    {pointsToNextLevel} points to Level {currentLevel + 1}
                  </p>
                </div>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-full rounded-full bg-violet-500 transition-all duration-500 ease-out"
                    style={{ width: `${Math.max(8, progress * 100)}%` }}
                    role="progressbar"
                    aria-valuenow={exp}
                    aria-valuemin={0}
                    aria-valuemax={nextLevelExp}
                  />
                </div>
              </div>
            </section>

            <section
              id="account-profile"
              data-account-section-id="profile"
              ref={registerSectionRef("profile")}
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <div className="mb-6">
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Profile</h2>
                <p className="mt-1.5 text-base text-slate-500">
                  Update how your account appears and adjust your personal details.
                </p>
              </div>
              <ProfileTab userData={userData} userEmail={userEmail} imageUrl={clerkUser?.imageUrl} />
            </section>

            <section
              id="account-family"
              data-account-section-id="family"
              ref={registerSectionRef("family")}
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <div className="mb-6">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-900">
                    {isFamilyPlan ? "Family plan" : "Plan"}
                  </h2>
                  {isFamilyPlan && (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                      Active
                    </span>
                  )}
                </div>
                <p className="mt-1.5 text-base text-slate-500">
                  {isChildUser
                    ? "You're part of a family plan. Your account is managed by your family admin."
                    : isFamilyPlan
                      ? "Manage who is protected and keep your household account organized."
                      : "Convert to a family plan to protect your loved ones with their own accounts."}
                </p>
              </div>
              {!isChildUser && <FamilySection userData={userData} userEmail={userEmail} />}
              {isChildUser && (
                <div className="space-y-7">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">Family members</h3>
                    {childFamilyLoading ? (
                      <div className="space-y-3">
                        <div className="h-[120px] rounded-2xl bg-slate-50 animate-pulse" />
                        <div className="h-[120px] rounded-2xl bg-slate-50 animate-pulse" />
                      </div>
                    ) : childFamilyData && childFamilyData.members.length > 0 ? (
                      <div className="space-y-3">
                        {childFamilyData.members.map((member) => (
                          <FamilyMemberExpanded key={member.id} member={member} />
                        ))}
                      </div>
                    ) : null}
                  </div>

                  <div className="border-t border-slate-100 pt-6">
                    <h3 className="mb-2 text-lg font-semibold text-red-800">Danger zone</h3>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="mb-1 text-sm text-red-900">
                          Deactivate your account and suspend access to all services.
                        </p>
                        <p className="text-sm text-red-700">
                          All your data and settings are preserved, but you lose access to the platform.
                        </p>
                      </div>
                      <Button
                        variant="danger"
                        className="whitespace-nowrap px-5 py-3"
                        onClick={() => {
                          setUserToDelete({ id: userData.id, email: userEmail, isSelf: true, isChild: true });
                          setShowDeleteConfirmModal(true);
                        }}
                        disabled={deleteUserMutation.isPending}
                      >
                        Deactivate Account
                      </Button>
                    </div>
                  </div>
                  <DeleteUserModal
                    showModal={showDeleteConfirmModal}
                    userToDelete={userToDelete}
                    userLicense={userToDelete ? userData.license : undefined}
                    onClose={() => setShowDeleteConfirmModal(false)}
                    onConfirm={confirmDeleteUser}
                    isLoading={deleteUserMutation.isPending}
                  />
                </div>
              )}
            </section>

            {!isChildUser && (
              <section
                id="account-subscription"
                data-account-section-id="subscription"
                ref={registerSectionRef("subscription")}
                className="scroll-mt-28 py-8 sm:py-10"
              >
                <SubscriptionTab userData={userData} />
              </section>
            )}

            {!isChildUser && (
              <section
                id="account-welcome-kit"
                data-account-section-id="welcomeKit"
                ref={registerSectionRef("welcomeKit")}
                className="scroll-mt-28 py-8 sm:py-10"
              >
                <WelcomeKitSection />
              </section>
            )}

            <section
              id="account-saved"
              data-account-section-id="saved"
              ref={registerSectionRef("saved")}
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Saved content</h2>
                  <p className="mt-1.5 text-base text-slate-500">
                    Review your bookmarked articles and return to them quickly.
                  </p>
                </div>
                <Link
                  to="/saved"
                  search={{ invitationcode: undefined, __clerk_ticket: undefined }}
                  className="group inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-xl border border-slate-200/60 px-3.5 py-2 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
                >
                  View all
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </div>
              <SavedContentSection />
            </section>

            <section
              id="account-preferences"
              data-account-section-id="preferences"
              ref={registerSectionRef("preferences")}
              className="scroll-mt-28 py-8 sm:py-10"
            >
              <div className="mb-6">
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Preferences</h2>
                <p className="mt-1.5 text-base text-slate-500">
                  Control communications and privacy settings.
                </p>
              </div>
              <PreferencesTab userData={userData} />
            </section>

            {!isChildUser && (
              <section
                id="account-danger-zone"
                className="scroll-mt-28 border-t border-slate-100 py-8 sm:py-10"
              >
                <h3 className="mb-2 text-lg font-semibold text-red-800">Danger zone</h3>
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="mb-1 text-sm text-red-900">
                      Deactivate your account and suspend access to all services.
                    </p>
                    <p className="text-sm text-red-700">
                      All your data and settings are preserved, but you lose access to the platform.
                    </p>
                  </div>
                  <Button
                    variant="danger"
                    className="whitespace-nowrap px-5 py-3"
                    onClick={() => {
                      setUserToDelete({ id: userData.id, email: userEmail, isSelf: true, isChild: false });
                      setShowDeleteConfirmModal(true);
                    }}
                    disabled={deleteUserMutation.isPending}
                  >
                    Deactivate Account
                  </Button>
                </div>
                <DeleteUserModal
                  showModal={showDeleteConfirmModal}
                  userToDelete={userToDelete}
                  userLicense={userToDelete ? userData.license : undefined}
                  onClose={() => setShowDeleteConfirmModal(false)}
                  onConfirm={confirmDeleteUser}
                  isLoading={deleteUserMutation.isPending}
                />
              </section>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
